import { useState } from "react";
import axios from "axios";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Specific stone", "Inventory request", "Bespoke sourcing", "Partnership", "Other"];

const FIELD =
  "border-white/15 bg-white/[0.04] text-white placeholder:text-[#717680] focus-visible:ring-[#C9A24B]/50 focus-visible:border-[#C9A24B]/60";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", interest: "Specific stone", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        interest: form.interest,
        message: form.message,
      });
      toast.success("Inquiry received — our desk will respond within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", interest: "Specific stone", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Could not send your inquiry — please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="contact-section" className="mx-auto max-w-7xl px-6 py-16 lg:px-12">
      <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
        <Reveal>
          <p className="eyebrow mb-4">Contact</p>
          <h2 className="font-display text-3xl font-light leading-snug tracking-tight text-white sm:text-4xl lg:text-5xl">
            Talk to <span className="gold-text italic">the desk</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[#B8BCC2]">
            Send a brief, a cert number, or a target price position. A trader — not a bot — replies
            within one business day.
          </p>

          <div className="mt-10 space-y-5">
            <a
              data-testid="contact-email-link"
              href="mailto:contact@sunraysorg.com"
              className="group flex items-center gap-4 text-[#B8BCC2] transition-colors hover:text-[#E8DCC0]"
            >
              <span className="glass-panel flex h-11 w-11 items-center justify-center rounded-full">
                <Mail className="h-4 w-4 text-[#C9A24B]" />
              </span>
              contact@sunraysorg.com
            </a>
            <div className="flex items-center gap-4 text-[#B8BCC2]" data-testid="contact-address">
              <span className="glass-panel flex h-11 w-11 items-center justify-center rounded-full">
                <MapPin className="h-4 w-4 text-[#C9A24B]" />
              </span>
              Jumeirah Lakes Towers, Dubai, UAE
            </div>
            <div className="flex items-center gap-4 text-[#717680]" data-testid="contact-phone">
              <span className="glass-panel flex h-11 w-11 items-center justify-center rounded-full">
                <Phone className="h-4 w-4 text-[#C9A24B]" />
              </span>
              Phone line announced at launch
            </div>
          </div>

          <div className="glass-panel mt-10 rounded-2xl p-6" data-testid="contact-hours">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#717680]">Desk hours</p>
            <p className="mt-2 text-sm text-[#B8BCC2]">Sunday — Thursday · 9:00 — 18:00 GST</p>
            <p className="mt-1 text-sm text-[#717680]">Viewings by appointment only</p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="glass-panel rounded-3xl p-8 lg:p-10" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-[11px] uppercase tracking-[0.25em] text-[#717680]">Name *</label>
                <Input data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={FIELD} />
              </div>
              <div>
                <label className="mb-2 block text-[11px] uppercase tracking-[0.25em] text-[#717680]">Email *</label>
                <Input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={FIELD} />
              </div>
              <div>
                <label className="mb-2 block text-[11px] uppercase tracking-[0.25em] text-[#717680]">Company</label>
                <Input data-testid="contact-input-company" value={form.company} onChange={set("company")} placeholder="Jewelry house / retailer" className={FIELD} />
              </div>
              <div>
                <label className="mb-2 block text-[11px] uppercase tracking-[0.25em] text-[#717680]">Phone</label>
                <Input data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="+971 ..." className={FIELD} />
              </div>
            </div>

            <div className="mt-6">
              <label className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-[#717680]">I'm interested in</label>
              <div className="flex flex-wrap gap-2" data-testid="contact-interest-chips">
                {INTERESTS.map((i) => (
                  <button
                    type="button"
                    key={i}
                    data-testid={`contact-interest-${i.toLowerCase().replace(/\s+/g, "-")}`}
                    data-active={form.interest === i}
                    onClick={() => setForm((f) => ({ ...f, interest: i }))}
                    className="chip"
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="mb-2 block text-[11px] uppercase tracking-[0.25em] text-[#717680]">Your brief *</label>
              <Textarea
                data-testid="contact-input-message"
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="e.g. 2.00–2.50ct round, G/VS1, GIA, Ideal cut — parcel of 5, delivery to Antwerp"
                className={FIELD}
              />
            </div>

            <button data-testid="contact-submit-btn" type="submit" disabled={sending} className="gold-btn mt-8 w-full disabled:opacity-60">
              {sending ? "Sending…" : "Send Inquiry"} <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-4 text-center text-[11px] text-[#717680]">
              No spam, no lists — your brief goes straight to the trading desk.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
